import { useClientSelector } from 'client/reducers';
import { selectCmsItems } from 'client/reducers/story/selectors';
import React from 'react';
import classNames from 'classnames';

import type { BBCommonProps } from 'types/story';
import { cutURLMethod } from 'common/utils/assets';
import { OTHER_PROPS } from 'common/constants/component-props';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { useExposeParams, useTransitionEndTimeout } from 'client/components/common/BuildingBlocks/utils/hooks';
import { ScriptRenderer } from 'client/components/common/ScriptRenderer/ScriptRenderer';
import { VideoPlayer } from 'common/components/VideoPlayer';
import cms from 'utils/cms';

import css from './Card.scss';

/**
 * Parses and resolves video property from CMS
 * @param prop - The video property to parse
 * @param cmsItems - CMS items for reference resolution
 * @returns Resolved video URL
 */
const parseVideoProp = (prop: string = '', cmsItems: ReturnType<typeof selectCmsItems>): string => {
	let result = prop || '';
	result = cms.resolveCmsReferenceValue(result, cmsItems);
	result = cutURLMethod(result);
	return result;
};

type CardProps = BBCommonProps;

const Card: React.FC<CardProps> = props => {
	const { uiConfig, stateAttrs, eventListeners, transitionCSS } = props;
	const cmsItems = useClientSelector(selectCmsItems);

	const componentProps = uiConfig.componentProps.other || {};
	const backgroundVideo = parseVideoProp(componentProps?.[OTHER_PROPS.card.backgroundVideo], cmsItems);
	const backgroundPoster = parseVideoProp(componentProps?.[OTHER_PROPS.card.backgroundPoster], cmsItems);

	const editableModeNodeProps = props.isEditableMode ? { ...props.editableModeProps?.nodeProps } : null;

	const { duration, delay } = useExposeParams({
		exposeEffect: props.uiConfig.expose?.effect,
		bbType: props.type,
		in: props.in,
	});

	useTransitionEndTimeout(props);

	const combinedStyles = {
		...uiConfig.nodeProps.style,
		...editableModeNodeProps?.style,
		...(transitionCSS ? { transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` } : null),
	};

	const combinedClassNames = classNames(
		css.card,
		transitionCSS,
		uiConfig.nodeProps.className,
		editableModeNodeProps?.className
	);

	const showBackgroundVideo = backgroundVideo && backgroundVideo !== 'none';

	return (
		<div
			{...uiConfig.nodeProps}
			{...stateAttrs}
			{...eventListeners}
			{...editableModeNodeProps}
			style={combinedStyles}
			className={combinedClassNames}
			ref={props.containerRef as React.RefObject<HTMLDivElement>}
		>
			<ScriptRenderer
				value={props.uiConfig.componentProps?.other?.embedCode ?? ''}
				disabled={props.isEditableMode}
				scriptOnly
			/>
			<div className={css.tint} style={{ background: uiConfig.componentProps.other[OTHER_PROPS.card.tint] }} />
			{showBackgroundVideo && (
				<VideoPlayer
					className={css.videoBg}
					src={backgroundVideo}
					poster={backgroundPoster}
					muted={uiConfig.componentProps.other[OTHER_PROPS.card.muted] ?? true}
					loop={uiConfig.componentProps.other[OTHER_PROPS.card.loop] ?? false}
					autoplay={!props.isEditableMode}
					controls={false}
				/>
			)}
		</div>
	);
};

export default withCardTransitionContext(Card);
